import React from 'react';
import { Users, Building2, Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import Icon from '../../../components/AppIcon';

const RoleSelectionCard = ({ selectedRole, onRoleChange, authorizedRole, error, userEmail }) => {
  const roles = [
    {
      id: 'student',
      title: 'Student',
      description: 'Access learning resources, team formation, and student-focused opportunities',
      icon: Users,
      color: 'blue',
      features: ['Learning Pathways', 'Team Formation', 'Student Dashboard', 'Peer Collaboration']
    },
    {
      id: 'city_official',
      title: 'City Official',
      description: 'Manage city initiatives, problem statements, and municipal partnerships',
      icon: Building2,
      color: 'green',
      features: ['City Dashboard', 'Problem Management', 'Municipal Tools', 'Public Services']
    },
    {
      id: 'pcf_admin',
      title: 'PCF Administrator',
      description: 'Platform administration, user management, and system oversight',
      icon: Shield,
      color: 'purple',
      features: ['Admin Dashboard', 'User Management', 'System Analytics', 'Content Moderation']
    }
  ];

  const handleRoleSelect = (roleId) => {
    onRoleChange(roleId);
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Role</h2>
        <p className="text-gray-600">Select the role that matches your email authorization</p>
        
        {userEmail && (
          <p className="text-sm text-muted-foreground mt-2">
            Selecting role for: <span className="font-medium">{userEmail}</span>
          </p>
        )}
      </div>

      {/* Role Authorization Status */}
      {authorizedRole && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-blue-800 mb-1">Email Verified</p>
              <p className="text-blue-700">
                Your email is authorized for the <strong className="capitalize">
                {authorizedRole?.replace('_', ' ')}</strong> role. Please select this role to continue.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Email Authorization Warning */}
      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium text-amber-800 mb-1">Role Verification Required</p>
            <p className="text-amber-700">
              You can only select the role that matches your email authorization. 
              If you need access to a different role, contact your administrator.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4">
        {roles?.map((role) => {
          const RoleIcon = role?.icon;
          const isSelected = selectedRole === role?.id;
          const isAuthorized = !authorizedRole || authorizedRole === role?.id;
          const isDisabled = authorizedRole && authorizedRole !== role?.id;
          
          return (
            <div
              key={role?.id}
              className={`cursor-pointer p-6 rounded-xl border-2 transition-all duration-200 ${
                isDisabled 
                  ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                  : isSelected
                  ? `border-${role?.color}-500 bg-${role?.color}-50`
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
              onClick={() => !isDisabled && handleRoleSelect(role?.id)}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${
                  isDisabled 
                    ? 'bg-gray-100' 
                    : `bg-${role?.color}-100`
                }`}>
                  <RoleIcon className={`w-6 h-6 ${
                    isDisabled 
                      ? 'text-gray-400' 
                      : `text-${role?.color}-600`
                  }`} />
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className={`text-lg font-semibold ${
                      isDisabled ? 'text-gray-400' : 'text-gray-900'
                    }`}>
                      {role?.title}
                    </h3>
                    
                    {isAuthorized && authorizedRole === role?.id && (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                    
                    {isDisabled && (
                      <Icon name="Lock" size={16} className="text-gray-400" />
                    )}
                  </div>

                  <p className={`mb-3 ${
                    isDisabled ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {role?.description}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {role?.features?.map((feature, index) => (
                      <span
                        key={index}
                        className={`px-2 py-1 text-xs rounded-full ${
                          isDisabled 
                            ? 'bg-gray-100 text-gray-400'
                            : `bg-${role?.color}-100 text-${role?.color}-700`
                        }`}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  {isDisabled && (
                    <p className="text-xs text-gray-400 mt-2 italic">
                      Not authorized for your email
                    </p>
                  )}
                </div>

                <div className="flex-shrink-0">
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    isSelected 
                      ? `border-${role?.color}-500 bg-${role?.color}-500` 
                      : isDisabled
                      ? 'border-gray-300 bg-gray-100' :'border-gray-300'
                  }`}>
                    {isSelected && (
                      <div className="w-full h-full rounded-full bg-white scale-50"></div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <Icon name="AlertCircle" size={16} className="text-red-600" />
            <p className="text-red-800 text-sm font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="text-center">
        <p className="text-xs text-muted-foreground">
          Need help? Contact{' '}
          <a href="mailto:admin@pcf.org" className="text-primary hover:underline">
            admin@pcf.org
          </a>{' '}
          for role access requests.
        </p>
      </div>
    </div>
  );
};

export default RoleSelectionCard;